import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import LoaderFullPage from "../../components/Loaders"
import logo from "../../assets/images/logo-white-text.png"
import AuthMessages from "../Login/components/AuthMessages"
import authStyles from "../../styles/Authentication.module.css"
import { useAuth } from "../../context/authContext"
import useAuthActionLink from "./hooks/useAuthActionLink"
import { LogInfo } from "../../helpers/logger"

export default function VerifyEmail() {
  const { auth } = useAuth()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)
  const [hasAppliedVerification, setHasAppliedVerification] = useState(false)
  const [err, setErr] = useState(null)
  const [msg, setMsg] = useState(null)

  // Keep verifier stable so the action-link hook does not restart on rerender.
  const verifyEmailActionCode = useCallback(
    async (code) => {
      const actionInfo = await auth.checkActionCode(code)

      if (actionInfo?.operation !== "VERIFY_EMAIL") {
        throw new Error("UNEXPECTED_ACTION_CODE_OPERATION")
      }

      return actionInfo
    },
    [auth],
  )

  const {
    oobCode,
    isVerifying,
    isVerified,
    verifiedPayload,
    verificationError,
  } =
    useAuthActionLink({
      expectedMode: "verifyEmail",
      verifyCode: verifyEmailActionCode,
      invalidLinkMessage: "This e-mail verification link is invalid or expired.",
    })

  const verifiedEmailForPrefill =
    verifiedPayload?.data?.email ||
    verifiedPayload?.email ||
    verifiedPayload?.data?.newEmail ||
    ""

  useEffect(() => {
    let cancelled = false

    const applyVerificationCode = async () => {
      if (!isVerified || !oobCode || hasAppliedVerification) return

      setLoading(true)
      setErr(null)
      setMsg(null)

      try {
        LogInfo("Applying email verification code")
        await auth.applyActionCode(oobCode)
        if (cancelled) return

        setHasAppliedVerification(true)
        setMsg("E-mail verified successfully. You can now sign in.")
      } catch (error) {
        if (cancelled) return
        setErr("Unable to verify e-mail. Please request a new verification link.")
      } finally {
        setLoading(false)
      }
    }

    applyVerificationCode()

    return () => {
      cancelled = true
    }
  }, [auth, hasAppliedVerification, isVerified, oobCode])

  const displayError = err || verificationError
  const displayMsg = displayError ? null : msg

  if (isVerifying || loading) return <LoaderFullPage />

  return (
    <div className="layout-login">
      <img src={logo} alt="Recovery Link Logo" className="main-logo" />
      <div className="login-container">
        <h2>Verify your e-mail</h2>
        <AuthMessages err={displayError} msg={displayMsg} logoutMsg={null} />
        <button
          className={`${authStyles.plainTextLink} ${authStyles.backLink}`}
          type="button"
          onClick={() => {
            navigate("/", {
              replace: true,
              state: {
                prefillEmail: verifiedEmailForPrefill || undefined,
              },
            })
          }}
          disabled={loading}
        >
          Back to Login
        </button>
      </div>
    </div>
  )
}